import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useGeoLanguage } from "../hooks/useGeoLanguage";
import Seo from "../components/Seo";
import LandingNavbar from "../components/landing/LandingNavbar";
import LandingHero from "../components/landing/LandingHero";
import LandingFeatures from "../components/landing/LandingFeatures";
import LandingShowcase from "../components/landing/LandingShowcase";
import LandingAppBadges from "../components/landing/LandingAppBadges";
import LandingHowItWorks from "../components/landing/LandingHowItWorks";
import LandingPricing from "../components/landing/LandingPricing";
import LandingTrustSecurity from "../components/landing/LandingTrustSecurity";
import LandingCTA from "../components/landing/LandingCTA";
import LandingFAQ from "../components/landing/LandingFAQ";
import LandingContactForm from "../components/landing/LandingContactForm";
import LandingFooter from "../components/landing/LandingFooter";

const SITE_URL = "https://mawered.com";

const HREFLANG_ALTERNATES = [
  { lang: "fr", href: SITE_URL + "/" },
  { lang: "en", href: SITE_URL + "/en/" },
  { lang: "ar", href: SITE_URL + "/ar/" },
  { lang: "x-default", href: SITE_URL + "/" },
];

const LANDING_JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Mawered",
    alternateName: "Teknovat",
    url: SITE_URL,
    logo: `${SITE_URL}/assets/landing/og-cover.png`,
    description:
      "Plateforme RH & paie multi-pays pour l'EMEA — Tunisie, Qatar, Maroc, France.",
    areaServed: ["TN", "QA", "MA", "FR"],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      availableLanguage: ["French", "English", "Arabic"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Mawered SIRH",
    url: SITE_URL,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    description:
      "Logiciel RH & paie multi-pays : pointage ZKTeco, congés, recrutement et gestion des talents pour l'EMEA.",
    offers: {
      "@type": "Offer",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
    },
    featureList: [
      "Gestion des présences & pointage ZKTeco",
      "Calcul de paie multi-pays (TN/MA/QA/SA/FR)",
      "Recrutement ATS",
      "Gestion des congés & absences",
      "Gestion des talents",
    ],
    provider: {
      "@type": "Organization",
      name: "Teknovat",
      url: SITE_URL,
    },
  },
];

const LandingPageEnriched = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").split("-")[0];

  // Anonymous visitors only (this page only renders when unauthenticated) —
  // defaults the language to the visitor's geographic zone instead of
  // leaving them stuck on the fallback French when their browser language
  // doesn't match fr/en/ar.
  useGeoLanguage();

  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <div className="dark min-h-screen" style={{ backgroundColor: "var(--lm-canvas)", fontFamily: "Inter, SF Pro Display, -apple-system, system-ui, sans-serif" }}>
      <Seo
        path="/"
        lang={lang}
        title="Mawered — Plateforme RH & Paie Multi-Pays pour l'EMEA"
        description="Mawered (Teknovat) : plateforme RH & paie multi-pays pour l'EMEA — Tunisie, Qatar, Maroc, France. Pointage ZKTeco, congés, recrutement et talents."
        alternates={HREFLANG_ALTERNATES}
        jsonLd={LANDING_JSON_LD}
      />
      <LandingNavbar />
      <LandingHero />
      <LandingFeatures />
      <LandingShowcase />
      <LandingAppBadges />
      <LandingHowItWorks />
      <LandingPricing />
      <LandingTrustSecurity />
      <LandingCTA />
      <LandingFAQ />
      <LandingContactForm />
      <LandingFooter />
    </div>
  );
};

export default LandingPageEnriched;
